import SearchSubmitButton from "../../../Component/UI/SearchSubmitButton";
import ContentState from "../../../Component/UI/ContentState";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import api from "../../../Component/axios_Interceptor/api";
import url from "../../../Component/server";
import { AdminIcon as Icon } from "../../../Component/style/common_style";
import Layout_Admin from "../../../Component/UI/Layout_Admin";
import AdminPageHeader from "../../../Component/UI/AdminPageHeader";
import PagingControl from "../../../Component/UI/PagingControl";
type StatusSummary = { order_status: string; order_count: number; detail_count: number };
type LiveItem = {
  seq: number;
  live_title: string;
  live_start: string;
  order_count: number;
  detail_count: number;
  statuses: StatusSummary[];
};

const PAGE_BY_STATUS: Record<string, string> = {
  결제대기: "payment-wait", 결제완료: "payment-done", 발주완료: "order-done",
  배송중: "shipping", 배송완료: "delivery-done", 주문취소: "cancel",
  환불처리: "refund", 사고처리: "accident",
};

const PER_PAGE = 10;

const DOW = ["일", "월", "화", "수", "목", "금", "토"];

/** "2026-06-03T22:00:00" → "2026.06.03(수) 22:00" */
const formatLiveDate = (iso: string) => {
  const date = iso.slice(0, 10);
  const time = iso.slice(11, 16);
  const dow = DOW[new Date(iso).getDay()];
  return `${date.replaceAll("-", ".")}(${dow}) ${time}`;
};

const OrderLiveList = () => {
  const Navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const targetStatuses = searchParams.get("statuses") || "";
  const targetTitle = searchParams.get("title") || "";
  const targetStart = searchParams.get("start_date") || "";
  const targetEnd = searchParams.get("end_date") || "";

  const [allLives, setAllLives] = useState<LiveItem[]>([]);
  const [excessLiveSeqs, setExcessLiveSeqs] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchDraft, setSearchDraft] = useState("");
  const [searchWord, setSearchWord] = useState("");
  const [startDt, setStartDt] = useState("");
  const [endDt, setEndDt] = useState("");
  const [sortAsc, setSortAsc] = useState(false); // false = 최신순(DESC)
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const summaryRequest = useRef(0);

  const get_live_list = async () => {
    const request = ++summaryRequest.current;
    setLoading(true);
    setLoadError(false);
    try {
      const res = await api.get(url.Communication_url.order_live_summary, {
        params: {
          ...(targetStatuses ? { statuses: targetStatuses } : {}),
          ...(targetStart ? { start_date: targetStart } : {}),
          ...(targetEnd ? { end_date: targetEnd } : {}),
        },
      });
      const grouped = new Map<number, LiveItem>();
      (Array.isArray(res.data) ? res.data : []).forEach((row: any) => {
        const seq = Number(row.live_seq);
        if (!Number.isSafeInteger(seq) || seq <= 0) return;
        const status = {
          order_status: String(row.order_status),
          order_count: Number(row.order_count) || 0,
          detail_count: Number(row.detail_count) || 0,
        };
        const current = grouped.get(seq);
        if (current) {
          current.statuses.push(status);
        } else {
          grouped.set(seq, {
            seq,
            live_title: String(row.live_title || "제목 없음"),
            live_start: String(row.live_start || ""),
            order_count: Number(row.live_order_count) || 0,
            detail_count: Number(row.live_detail_count) || 0,
            statuses: [status],
          });
        }
      });
      if (request === summaryRequest.current) setAllLives([...grouped.values()]);
    } catch (error) {
      console.error("라이브 목록 로드 실패:", error);
      if (request === summaryRequest.current) setLoadError(true);
    } finally {
      if (request === summaryRequest.current) setLoading(false);
    }
  };

  useEffect(() => {
    void get_live_list();
    api
      .get(url.Communication_url.oversell_notification)
      .then((res) => {
        if (res.data && Array.isArray(res.data)) {
          const seqs = res.data.map((item: any) => Number(item.live_seq)).filter(Boolean);
          setExcessLiveSeqs(seqs);
        }
      })
      .catch(() => {});
    return () => { summaryRequest.current += 1; };
  }, [targetStatuses, targetStart, targetEnd]);

  // 검색어/기간 변경 시 1페이지로
  const resetPage = () => setCurrentPage(1);
  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchDraft(e.target.value);
  };
  const onStart = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDt(e.target.value);
    resetPage();
  };
  const onEnd = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDt(e.target.value);
    resetPage();
  };
  const reset = () => {
    setSearchDraft("");
    setSearchWord("");
    setStartDt("");
    setEndDt("");
    setSortAsc(false);
    resetPage();
  };

  // 검색 + 기간 필터 + 정렬 (클라)
  const filtered = useMemo(() => {
    let arr = allLives.filter((l) =>
      l.live_title.toLowerCase().includes(searchWord.toLowerCase())
    );
    if (startDt) arr = arr.filter((l) => l.live_start.slice(0, 10) >= startDt);
    if (endDt) arr = arr.filter((l) => l.live_start.slice(0, 10) <= endDt);
    arr = [...arr].sort((a, b) =>
      sortAsc
        ? a.live_start.localeCompare(b.live_start)
        : b.live_start.localeCompare(a.live_start)
    );
    return arr;
  }, [allLives, searchWord, startDt, endDt, sortAsc]);

  const paged = filtered.slice(
    (currentPage - 1) * PER_PAGE,
    currentPage * PER_PAGE
  );

  // 라이브 클릭 → 주문내역으로 이동 (live_seq 전달 — 백엔드 필터 대비)
  const goOrders = (seq: number, status?: string) => {
    const page = PAGE_BY_STATUS[status || ""] || (targetStatuses.split(",").length === 1
      ? PAGE_BY_STATUS[targetStatuses]
      : "all") || "all";
    Navigate(`/admin/orderlist/${page}?live_seq=${seq}`);
  };

  const statusColumns = useMemo(() => {
    const present = new Set(allLives.flatMap(live => live.statuses.map(status => status.order_status)));
    const known = Object.keys(PAGE_BY_STATUS).filter(status => present.has(status));
    return [...known, ...Array.from(present).filter(status => !(status in PAGE_BY_STATUS))];
  }, [allLives]);

  const totalOrderCount = filtered.reduce((sum, live) => sum + live.order_count, 0);
  const totalDetailCount = filtered.reduce((sum, live) => sum + live.detail_count, 0);

  return (
    <Layout_Admin menuname={"주문내역"}>
      <AdminPageHeader title={targetTitle || "주문 목록"} description="라이브별 주문 내역을 확인합니다." actions={<Total>
          라이브 <b>{filtered.length.toLocaleString()}</b>개 · 주문 <b>{totalOrderCount.toLocaleString()}</b>건 · 주문상세 <b>{totalDetailCount.toLocaleString()}</b>건
        </Total>} />

      <TopRow>

        <SearchArea data-admin-search="" role="search" aria-label="라이브별 주문 검색" onSubmit={event => { event.preventDefault(); setSearchWord(searchDraft.trim()); resetPage(); }} onKeyDown={event => { if (event.key === "Enter" && (event.nativeEvent.isComposing || event.keyCode === 229)) event.preventDefault(); }}>
          <SearchBox>
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              aria-label="라이브 제목 검색"
              placeholder="라이브 제목을 입력해주세요."
              value={searchDraft} enterKeyHint="search"
              onChange={onSearch}
            />
          </SearchBox>
          <DateRange>
            <DateInput aria-label="조회 시작일" type="date" value={startDt} onChange={onStart} />
            <span className="tilde" aria-hidden="true">~</span>
            <DateInput aria-label="조회 종료일" type="date" value={endDt} onChange={onEnd} />
          </DateRange>
          <SearchSubmitButton type="submit">검색</SearchSubmitButton>
          <ResetBtn data-admin-search-reset="" type="button" aria-label="검색 조건 초기화" onClick={reset}>
            <Icon src={require("../../../Image/ic_reset_black.png")} alt="" />
          </ResetBtn>
        </SearchArea>
      </TopRow>

      <TableScroll role="region" aria-label="라이브별 주문 목록" tabIndex={0}>
      {loadError ? (
        <ContentState kind="error" icon="bag" title="주문 목록을 불러오지 못했습니다." action={{ label: "다시 불러오기", onClick: get_live_list }} />
      ) : loading ? (
        <ContentState kind="loading" icon="bag" title="주문 목록을 불러오고 있습니다." />
      ) : paged.length === 0 ? (
        <ContentState icon="bag" title="주문 내역이 있는 라이브가 없습니다." description="라이브에서 주문이 접수되면 이곳에 표시됩니다." />
      ) : (
        <OrderTable $statusCount={statusColumns.length}>
          <thead>
            <tr>
              <th scope="col" className="live-column">라이브 제목</th>
              <th scope="col" className="date-column">
                  <SortButton type="button" onClick={() => setSortAsc(v => !v)} aria-label={`라이브 일시 ${sortAsc ? '최신순' : '오래된순'} 정렬`}>
                    라이브 일시
                    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M8 4v16m0 0-4-4m4 4 4-4M16 20V4m0 0-4 4m4-4 4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </SortButton>
              </th>
              {statusColumns.map(status => <th key={status} scope="col">{status}</th>)}
            </tr>
          </thead>
          <tbody>
            {paged.map(data => (
              <tr key={data.seq} onClick={() => goOrders(data.seq)}>
                <th scope="row" className="live-column">
                  <div className="title">
                    <button type="button" className="live-title">{data.live_title}</button>
                    {excessLiveSeqs.includes(data.seq) && <ExcessMark title="재고 초과 주문건 발생">!</ExcessMark>}
                  </div>
                </th>
                <td className="date-column">
                  <time className="time" dateTime={data.live_start}>{formatLiveDate(data.live_start)}</time>
                </td>
                {statusColumns.map(statusName => {
                  const status = data.statuses.find(item => item.order_status === statusName);
                  return (
                    <td key={statusName} className={status ? 'status-cell' : 'empty-cell'}>
                      {status ? (
                        <StatusButton type="button" data-status={statusName}
                          aria-label={`${statusName} ${status.order_count}건 / 상세 ${status.detail_count}건`}
                          onClick={event => { event.stopPropagation(); goOrders(data.seq, statusName); }}>
                          <span className="mobile-status">{statusName}</span>
                          <span className="order-count">{status.order_count.toLocaleString()}<span className="unit">건</span></span>
                          <span className="detail-count">상세 {status.detail_count.toLocaleString()}건</span>
                        </StatusButton>
                      ) : <span className="empty" aria-label={`${statusName} 주문 없음`}>—</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </OrderTable>
      )}

      </TableScroll>

      <PagingControl
        cnt={filtered.length}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />
    </Layout_Admin>
  );
};

export default OrderLiveList;

const TopRow = styled.div`
  margin-block: 0 var(--space-6);
  padding: var(--space-4);
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-lg);
  background: var(--color-bg-surface);
`;

const Total = styled.div`
  color: var(--color-text-secondary);
  font-size: var(--text-caption);
  line-height: var(--leading-caption);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  b { color: var(--color-text-primary); font-weight: var(--font-weight-semibold); }
  @media (max-width: 560px) { white-space: normal; }
`;

const SearchArea = styled.form`
  display: grid;
  grid-template-columns: minmax(12rem, 1fr) minmax(19rem, auto) auto auto;
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
  > button { height: var(--control-lg); }
  .tilde { color: var(--color-text-secondary); }
  @media (max-width: 1050px) {
    grid-template-columns: minmax(0, 1fr) auto auto;
    > div:first-child { grid-column: 1 / -1; }
  }
  @media (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr) auto;
    > div { grid-column: 1 / -1; }
  }
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
  height: var(--control-lg);
  box-sizing: border-box;
  padding-inline: var(--space-3);
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-md);
  background: var(--color-bg-page);
  color: var(--color-text-secondary);
  &:hover { border-color: var(--color-border-strong); }
  &:focus-within { border-color: var(--color-brand); outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
  svg { flex-shrink: 0; }
  input {
    flex: 1; min-width: 0; border: 0; outline: 0; padding: 0;
    font: inherit; font-size: var(--text-body); color: var(--color-text-primary); background: transparent;
    caret-color: var(--color-brand);
    &::placeholder { color: var(--color-text-secondary); }
  }
`;

const DateRange = styled.div`
  display: flex; min-width: 0; align-items: center; gap: var(--space-2);
  @media (max-width: 560px) {
    display: grid; grid-template-columns: minmax(0, 1fr);
    .tilde { display: none; }
    > input { width: 100%; }
  }
`;

const DateInput = styled.input`
  flex: 1; width: 9rem; min-width: 0; height: var(--control-lg); box-sizing: border-box;
  padding-inline: var(--space-3);
  border: 1px solid var(--color-border-default); border-radius: var(--radius-md);
  font: inherit; font-size: var(--text-body); color: var(--color-text-primary); background: var(--color-bg-page);
  &:hover { border-color: var(--color-border-strong); }
  &:focus-visible { border-color: var(--color-brand); outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
`;

const ResetBtn = styled.button`
  display: inline-flex; align-items: center; justify-content: center;
  width: var(--control-lg); height: var(--control-lg); box-sizing: border-box; padding: 0;
  border: 1px solid var(--color-border-default); border-radius: var(--radius-md);
  background: var(--color-bg-page); color: var(--color-text-primary); cursor: pointer;
  img { width: var(--space-5); height: var(--space-5); }
  &:hover { background: var(--color-bg-subtle); border-color: var(--color-border-strong); }
  &:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
`;

const OrderTable = styled.table<{ $statusCount: number }>`
  width: 100%; min-width: ${p => 25 + p.$statusCount * 5}rem;
  border-collapse: separate; border-spacing: 0; table-layout: fixed;
  color: var(--color-text-primary); font-size: var(--text-body);
  font-variant-numeric: tabular-nums;
  th, td { padding: var(--space-3) var(--space-2); text-align: center; vertical-align: middle; }
  thead th {
    background: var(--color-bg-muted); color: var(--color-text-secondary);
    font-size: var(--text-caption); font-weight: var(--font-weight-medium); white-space: nowrap;
    border-bottom: 1px solid var(--color-border-default);
  }
  .live-column { width: 28%; padding-inline: var(--space-4); text-align: left; }
  .date-column { width: 10rem; white-space: nowrap; }
  tbody th, tbody td { border-bottom: 1px solid var(--color-border-default); }
  tbody tr:last-child > * { border-bottom: 0; }
  tbody tr { cursor: pointer; }
  tbody tr:hover, tbody tr:focus-within { background: var(--color-bg-subtle); }
  .title { display: flex; align-items: center; gap: var(--space-2); }
  .live-title {
    border: 0; padding: 0; min-width: 0; text-align: left;
    color: inherit; background: transparent; cursor: pointer;
    font: inherit; font-weight: var(--font-weight-semibold); line-height: var(--leading-body);
    overflow-wrap: anywhere;
    &:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: var(--space-1); border-radius: var(--radius-sm); }
  }
  .time { display: block; font-size: var(--text-caption); font-weight: var(--font-weight-regular); color: var(--color-text-secondary); }
  .empty { color: var(--color-text-disabled); font-size: var(--text-caption); }
  @container (max-width: 42rem) {
    display: block; min-width: 0; width: 100%;
    thead, tbody { display: block; }
    thead tr { display: flex; align-items: center; justify-content: space-between; background: var(--color-bg-muted); border-bottom: 1px solid var(--color-border-default); }
    thead th:not(.live-column):not(.date-column) { display: none; }
    thead .live-column, thead .date-column { display: block; width: auto; border: 0; }
    tbody tr {
      display: grid; grid-template-columns: repeat(2, minmax(0, 1fr));
      padding: var(--space-4); gap: var(--space-2);
      border-bottom: 1px solid var(--color-border-default);
    }
    tbody tr:last-child { border-bottom: 0; }
    tbody th, tbody td { border: 0; padding: 0; }
    tbody .live-column { grid-column: 1 / -1; width: auto; padding: 0; }
    tbody .date-column { grid-column: 1 / -1; width: auto; padding: 0 0 var(--space-2); text-align: left; }
    .live-title { min-height: var(--touch-target); }
    .empty-cell { display: none; }
  }
`;

const SortButton = styled.button`
  display: inline-flex; align-items: center; gap: var(--space-1);
  min-height: var(--control-sm); border: 0; padding: 0; background: transparent;
  color: var(--color-text-secondary); font: inherit; cursor: pointer;
  &:hover { color: var(--color-text-primary); }
  &:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
  @media (max-width: 760px) { min-height: var(--touch-target); }
`;

const ExcessMark = styled.span`
  display: inline-flex; flex-shrink: 0; align-items: center; justify-content: center;
  width: var(--space-5); height: var(--space-5); border-radius: var(--radius-pill);
  background: var(--color-danger-subtle); color: var(--color-danger-strong);
  font-size: var(--text-micro); font-weight: var(--font-weight-bold);
`;

const StatusButton = styled.button`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: var(--space-1); width: 100%; min-height: var(--control-lg);
  padding: var(--space-2) var(--space-1);
  border: 1px solid transparent; border-radius: var(--radius-md);
  background: transparent; color: var(--color-text-secondary);
  font: inherit; line-height: var(--leading-caption); cursor: pointer; white-space: nowrap;
  .mobile-status { display: none; }
  .order-count { font-size: var(--text-h3); font-weight: var(--font-weight-semibold); }
  .unit { margin-left: var(--space-1); font-size: var(--text-caption); font-weight: var(--font-weight-medium); }
  .detail-count { color: var(--color-text-secondary); font-size: var(--text-caption); }
  &[data-status='결제대기'] { color: var(--color-warning); }
  &:is([data-status='결제완료'],[data-status='발주완료'],[data-status='배송중']) { color: var(--color-text-link); }
  &[data-status='배송완료'] { color: var(--color-success); }
  &:is([data-status='주문취소'],[data-status='환불처리'],[data-status='사고처리']) { color: var(--color-danger-strong); }
  &:hover { background: var(--color-bg-surface); border-color: currentColor; }
  &:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
  @container (max-width: 42rem) {
    padding: var(--space-3) var(--space-2); border-color: var(--color-border-default);
    background: var(--color-bg-surface);
    .mobile-status { display: block; font-size: var(--text-caption); font-weight: var(--font-weight-medium); }
  }
`;

const TableScroll = styled.div`
  container-type: inline-size;
  min-width: 0; max-width: 100%; border: 1px solid var(--color-border-default);
  border-radius: var(--radius-lg); overflow-x: auto;
  scrollbar-color: var(--color-border-strong) var(--color-bg-page); scrollbar-width: thin;
  &:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
  ::selection { background: var(--color-brand-subtle); color: var(--color-text-primary); }
`;
